import {
  ButtonGroup,
  chakra,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Tag,
  TagLabel,
  Textarea,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { AudienceDTO } from "@intentsify/dto";
import {
  Audience,
  AudiencePricingStructure,
  AudienceType,
  AudienceTypeValue,
  Criteria,
  criteriaToSegmentLabels,
  DSP,
} from "@intentsify/types";
import { Button, Select } from "components";
import { useEffect } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { CatalogManagerScreenDefinition } from "screens";
import { useComponentColors } from "theme";
import { getZodDefaults } from "utils/getZodDefaults";
import { z } from "zod";
import {
  audienceTargetOptions,
  audienceTypeDesc,
  audienceTypeOptions,
  categoryOptions,
  criteriaOptions,
  destinations,
  generateOptionsForLabels,
  YesNoSelectOptions,
} from "../screens/CreateAudience/utils/selectValues";
import { liverampColor } from "../utils/utils";
import { ActivationDestinationFieldSet } from "./ActivationDestinationFieldSet";

type Props = {
  audience?: Audience;
  mode: "create" | "edit";
  isLoading: boolean;
  onCreate?: (params: AudienceDTO) => void;
  onEdit?: ({ id, data }: { id: number; data: AudienceDTO }) => void;
};

export const audienceFromSchema = z.object({
  audienceType: z
    .nativeEnum(AudienceType, {
      invalid_type_error: "Audience Type must be valid",
      required_error: "Audience Type is required",
    })
    .default(AudienceType.CUSTOM),
  audienceTarget: z
    .string({ required_error: "Field cannot be empty" })
    .default("B2B"),
  category: z.array(z.string({ required_error: "Field cannot be empty" })),
  segmentLabel: z.string({ required_error: "Field cannot be empty" }),
  fullSegmentName: z.string({ required_error: "Field cannot be empty" }),
  liverampSegmentName: z.string({ required_error: "Field cannot be empty" }),
  segmentDescription: z.string({ required_error: "Field cannot be empty" }),
  digitalAdTargeting: z.boolean().default(false),
  audienceExtension: z.boolean().default(false),
  campaignAnalytics: z.boolean().default(false),
  tvTargeting: z.boolean().default(false),
  measurementUse: z.boolean().default(false),
  distributed: z.boolean().default(false),
  custom: z.boolean().default(false),
  allowedPlatforms: z.string().default(""),
  sellerApproval: z.boolean().default(true),
  scaleGauge: z.number().int().nullable(),
  liverampAudienceId: z.string().nullable(),
  liverampAudienceSegmentId: z.string().nullable(),
  marketingHeadline: z.string().nullable(),
  readyForDistribution: z.boolean().nullable(),
  numberOfPlatforms: z.number().int().nullable(),
  numberOfPlatformsReady: z.number().int().nullable(),
  intentModelId: z.number().int().nullable(),
  avgCompetitorCpm: z.string().nullable(),
  avgCompetitorMediaPct: z.string().nullable(),
  percentMedia: z.string().nullable(),
  sellerComments: z.string().nullable(),
  blockedAdvertisers: z.string().nullable(),
  activationDestinations: z
    .array(
      z.object({
        dsp: z.nativeEnum(DSP).optional(),
        digitalAdCpm: z.string().default(""),
        contentMarketingCpm: z.string().default(""),
        tvTargetingCpm: z.string().default(""),
        cpm: z.string().default(""),
        programmaticPercent: z.string().default(""),
        cpmCap: z.string().default(""),
        advertiserDirectPercent: z.string().default(""),
        pricingStructure: z
          .array(z.nativeEnum(AudiencePricingStructure))
          .optional(),
      })
    )
    .nullable()
    .optional(),
  marketingText: z.string().nullable(),
  marketingCopy: z.string().nullable(),
  criteria: z.string({ required_error: "Field cannot be empty" }),
});

export const defaultFormValues = getZodDefaults(audienceFromSchema);

export type AudienceFormState = z.infer<typeof audienceFromSchema>;

const AudienceForm = ({
  mode,
  audience,
  isLoading,
  onCreate = () => ({}),
  onEdit = () => ({}),
}: Props) => {
  const componentColors = useComponentColors();
  const navigate = useNavigate();

  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    setValue,
    watch,
  } = useForm<AudienceFormState>({
    mode: "all",
    resolver: zodResolver(audienceFromSchema),
    defaultValues: {
      ...defaultFormValues,
      ...audience,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "activationDestinations",
  });

  const criteria = watch("criteria");
  const segmentLabel = watch("segmentLabel");
  const audienceType = watch("audienceType");
  const allowedPlatforms = watch("allowedPlatforms");
  const digitalAdTargeting = watch("digitalAdTargeting");

  useEffect(() => {
    let name = "Intentsify > ";

    if (audienceType) {
      name += `${audienceTypeDesc[audienceType]} Audience > `;
    }

    if (criteria) {
      name += `${criteria} > `;
    }

    if (segmentLabel) {
      name += segmentLabel;
    }

    setValue("fullSegmentName", name);
  }, [criteria, audienceType, segmentLabel, setValue]);

  return (
    <chakra.form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit((data) => {
        if (mode === "create") {
          onCreate({ ...data, criteria: data.criteria as Criteria });
        } else {
          onEdit({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            id: audience!.id,
            data: { ...data, criteria: data.criteria as Criteria },
          });
        }
      })}
      borderRadius={4}
    >
      <FormControl
        isInvalid={Boolean(errors?.audienceType?.message)}
        w="400px"
        isRequired
      >
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Audience
        </FormLabel>
        <Controller
          name="audienceType"
          control={control}
          render={({ field }) => (
            <Select
              size="md"
              isMulti={false}
              isClearable={false}
              allowMultilineOptions={true}
              placeholder="Select type of audience"
              options={audienceTypeOptions}
              value={audienceTypeOptions.find((c) => c.value === field.value)}
              onChange={(option) => {
                if (!option) {
                  return;
                }

                setValue("audienceType", option.value as AudienceTypeValue);
                setValue("custom", option.value === AudienceType.CUSTOM);
              }}
            />
          )}
        />
        <FormErrorMessage>{errors?.audienceType?.message}</FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={Boolean(errors?.audienceTarget?.message)}
        w="400px"
        isRequired
      >
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Type
        </FormLabel>
        <Controller
          name="audienceTarget"
          control={control}
          render={({ field }) => (
            <Select
              size="md"
              isMulti={false}
              isClearable={false}
              allowMultilineOptions={true}
              placeholder=""
              options={audienceTargetOptions}
              value={audienceTargetOptions.find((c) => c.value === field.value)}
              onChange={(option) => {
                if (!option) {
                  return;
                }

                setValue("audienceTarget", option.value);
              }}
            />
          )}
        />
        <FormErrorMessage>{errors?.audienceTarget?.message}</FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={Boolean(errors?.category?.message)}
        isRequired
        w="400px"
      >
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Category
        </FormLabel>
        <Controller
          name="category"
          control={control}
          render={({ field }) => (
            <Select
              size="md"
              isMulti={true}
              isClearable={false}
              allowMultilineOptions={true}
              placeholder="Select Category"
              options={categoryOptions}
              value={categoryOptions.filter((c) => {
                if (!field.value) {
                  return false;
                }
                return field.value.includes(c.value);
              })}
              onChange={(options) => {
                if (!options) {
                  return;
                }

                setValue(
                  "category",
                  options.map((opt) => opt.value)
                );
              }}
            />
          )}
        />
        <FormErrorMessage>{errors?.category?.message}</FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={Boolean(errors?.category?.message)}
        isRequired
        w="400px"
      >
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Criteria
        </FormLabel>
        <Controller
          name="criteria"
          control={control}
          render={({ field }) => (
            <Select
              size="md"
              isMulti={false}
              isClearable={false}
              allowMultilineOptions={true}
              placeholder="Select Criteria"
              options={criteriaOptions}
              value={criteriaOptions.find((c) => c.value === field.value)}
              onChange={(option) => {
                if (!option) {
                  return;
                }

                setValue("criteria", option.value);
                setValue("segmentLabel", "");
              }}
            />
          )}
        />
        <FormErrorMessage>{errors?.category?.message}</FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={Boolean(errors?.segmentLabel?.message)}
        w="400px"
        isRequired
      >
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Segment Labels
        </FormLabel>
        <Controller
          name="segmentLabel"
          control={control}
          render={({ field }) => (
            <Select
              size="md"
              isMulti={false}
              isClearable={false}
              allowMultilineOptions={true}
              placeholder="Select segments"
              options={generateOptionsForLabels(
                criteriaToSegmentLabels[criteria as Criteria]
              )}
              value={generateOptionsForLabels(
                criteriaToSegmentLabels[criteria as Criteria]
              ).find((c) => c.value === field.value)}
              onChange={(option) => {
                if (!option) {
                  return;
                }

                setValue("segmentLabel", option.value);
              }}
            />
          )}
        />
        <FormErrorMessage>{errors?.segmentLabel?.message}</FormErrorMessage>
      </FormControl>

      <FormControl
        w="400px"
        mt={4}
        isInvalid={!!errors?.fullSegmentName?.message}
        isRequired
      >
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Full Segment Name
        </FormLabel>
        <Textarea
          {...register("fullSegmentName")}
          placeholder="Full Segment Name"
          shadow="sm"
          size="sm"
          rounded="md"
        />
        <FormErrorMessage>{errors?.fullSegmentName?.message}</FormErrorMessage>
      </FormControl>

      <FormControl
        w="400px"
        mt={4}
        isInvalid={!!errors?.liverampSegmentName?.message}
        isRequired
      >
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Liveramp Segment Name
        </FormLabel>
        <Textarea
          {...register("liverampSegmentName")}
          placeholder="Liveramp Segment Name"
          shadow="sm"
          size="sm"
          rounded="md"
        />
        <FormErrorMessage>
          {errors?.liverampSegmentName?.message}
        </FormErrorMessage>
      </FormControl>

      <FormControl
        w="400px"
        mt={4}
        isInvalid={!!errors?.segmentDescription?.message}
        isRequired
      >
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Segment Description
        </FormLabel>
        <Textarea
          {...register("segmentDescription")}
          placeholder="Segment Description"
          shadow="sm"
          size="sm"
          rounded="md"
        />
        <FormErrorMessage>
          {errors?.segmentDescription?.message}
        </FormErrorMessage>
      </FormControl>

      <FormControl w="400px" mt={4}>
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Data Source
        </FormLabel>
        <Input
          type="text"
          value="1"
          shadow="sm"
          size="sm"
          rounded="md"
          isDisabled
        />
      </FormControl>

      <FormControl w="400px" mt={4}>
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Data Source Detail
        </FormLabel>
        <Input
          type="text"
          value="Known Data"
          shadow="sm"
          size="sm"
          rounded="md"
          isDisabled
        />
      </FormControl>

      <FormControl
        mt={4}
        isInvalid={Boolean(errors?.digitalAdTargeting?.message)}
        w="400px"
        isRequired
      >
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Digital Ad Targeting Use Permitted
        </FormLabel>
        <Controller
          name="digitalAdTargeting"
          control={control}
          render={({ field }) => (
            <Select
              size="md"
              isMulti={false}
              isClearable={false}
              allowMultilineOptions={true}
              placeholder=""
              options={YesNoSelectOptions}
              value={YesNoSelectOptions.find(
                (c) => c.value === Number(field.value)
              )}
              onChange={(option) => {
                if (!option) {
                  return;
                }

                setValue("digitalAdTargeting", Boolean(option.value));
              }}
            />
          )}
        />
        <FormErrorMessage>
          {errors?.digitalAdTargeting?.message}
        </FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={Boolean(errors?.audienceExtension?.message)}
        w="400px"
        isRequired
      >
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Audience Extension Permitted
        </FormLabel>
        <Controller
          name="audienceExtension"
          control={control}
          render={({ field }) => (
            <Select
              size="md"
              isMulti={false}
              isClearable={false}
              allowMultilineOptions={true}
              placeholder=""
              options={YesNoSelectOptions}
              value={YesNoSelectOptions.find(
                (c) => c.value === Number(field.value)
              )}
              onChange={(option) => {
                if (!option) {
                  return;
                }

                setValue("audienceExtension", Boolean(option.value));
              }}
            />
          )}
        />
        <FormErrorMessage>
          {errors?.audienceExtension?.message}
        </FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={Boolean(errors?.campaignAnalytics?.message)}
        w="400px"
        isRequired
      >
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Campaign Analytics Permitted
        </FormLabel>
        <Controller
          name="campaignAnalytics"
          control={control}
          render={({ field }) => (
            <Select
              size="md"
              isMulti={false}
              isClearable={false}
              allowMultilineOptions={true}
              placeholder=""
              options={YesNoSelectOptions}
              value={YesNoSelectOptions.find(
                (c) => c.value === Number(field.value)
              )}
              onChange={(option) => {
                if (!option) {
                  return;
                }

                setValue("campaignAnalytics", Boolean(option.value));
              }}
            />
          )}
        />
        <FormErrorMessage>
          {errors?.campaignAnalytics?.message}
        </FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={Boolean(errors?.tvTargeting?.message)}
        isRequired
        w="400px"
      >
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          TV Targeting Use Permitted
        </FormLabel>
        <Controller
          name="tvTargeting"
          control={control}
          render={({ field }) => (
            <Select
              size="md"
              isMulti={false}
              isClearable={false}
              allowMultilineOptions={true}
              placeholder=""
              options={YesNoSelectOptions}
              value={YesNoSelectOptions.find(
                (c) => c.value === Number(field.value)
              )}
              onChange={(option) => {
                if (!option) {
                  return;
                }

                setValue("tvTargeting", Boolean(option.value));
              }}
            />
          )}
        />
        <FormErrorMessage>{errors?.tvTargeting?.message}</FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={Boolean(errors?.measurementUse?.message)}
        isRequired
        w="400px"
      >
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Measurement Use Permitted
        </FormLabel>
        <Controller
          name="measurementUse"
          control={control}
          render={({ field }) => (
            <Select
              size="md"
              isMulti={false}
              isClearable={false}
              allowMultilineOptions={true}
              placeholder=""
              options={YesNoSelectOptions}
              value={YesNoSelectOptions.find(
                (c) => c.value === Number(field.value)
              )}
              onChange={(option) => {
                if (!option) {
                  return;
                }

                setValue("measurementUse", Boolean(option.value));
              }}
            />
          )}
        />
        <FormErrorMessage>{errors?.measurementUse?.message}</FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={Boolean(errors?.distributed?.message)}
        isRequired
        w="400px"
      >
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Has Been Distributed
        </FormLabel>
        <Controller
          name="distributed"
          control={control}
          render={({ field }) => (
            <Select
              size="md"
              isMulti={false}
              isClearable={false}
              allowMultilineOptions={true}
              placeholder=""
              options={YesNoSelectOptions}
              value={YesNoSelectOptions.find(
                (c) => c.value === Number(field.value)
              )}
              onChange={(option) => {
                if (!option) {
                  return;
                }

                setValue("distributed", Boolean(option.value));
              }}
            />
          )}
        />
        <FormErrorMessage>{errors?.distributed?.message}</FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={Boolean(errors?.custom?.message)}
        isRequired
        w="400px"
      >
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Activation Destinations
        </FormLabel>
        <>
          <Button
            onClick={() =>
              append({
                dsp: undefined,
                digitalAdCpm: "",
                contentMarketingCpm: "",
                tvTargetingCpm: "",
                cpm: "",
                programmaticPercent: "",
                cpmCap: "",
                advertiserDirectPercent: "",
                pricingStructure: undefined,
              })
            }
          >
            Add Activation Destination
          </Button>
          {fields.map((item, index) => (
            <ActivationDestinationFieldSet
              key={item.id}
              index={index}
              register={register}
              remove={remove}
              control={control}
              setValue={setValue}
              digitalAdTargeting={digitalAdTargeting}
            />
          ))}
        </>
      </FormControl>

      <FormControl
        isInvalid={Boolean(errors?.custom?.message)}
        isRequired
        mt={4}
        w="400px"
      >
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Custom
        </FormLabel>
        <Controller
          name="custom"
          control={control}
          render={({ field }) => (
            <Select
              size="md"
              isMulti={false}
              isClearable={false}
              allowMultilineOptions={true}
              placeholder=""
              options={YesNoSelectOptions}
              value={YesNoSelectOptions.find(
                (c) => c.value === Number(field.value)
              )}
              onChange={(option) => {
                if (!option) {
                  return;
                }

                setValue("custom", Boolean(option.value));
              }}
            />
          )}
        />
        <FormErrorMessage>{errors?.custom?.message}</FormErrorMessage>
      </FormControl>

      <FormControl
        w="400px"
        mt={4}
        isInvalid={!!errors?.allowedPlatforms?.message}
      >
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Allowed Platforms
        </FormLabel>
        <Input
          {...register("allowedPlatforms")}
          type="text"
          placeholder=""
          shadow="sm"
          size="sm"
          rounded="md"
        />
        <FormErrorMessage>{errors?.allowedPlatforms?.message}</FormErrorMessage>
      </FormControl>

      <FormControl w="400px" mt={4}>
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Blocked Advertisers
        </FormLabel>
        <Input
          {...register("blockedAdvertisers")}
          isDisabled={audienceType !== AudienceType.SYNDICATED}
          type="text"
          shadow="sm"
          size="sm"
          rounded="md"
        />
      </FormControl>

      <FormControl w="400px" mt={4}>
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Seller Comments
        </FormLabel>
        <Input
          {...register("sellerComments")}
          type="text"
          shadow="sm"
          size="sm"
          rounded="md"
        />
        <FormErrorMessage>{errors?.sellerComments?.message}</FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={Boolean(errors?.sellerApproval?.message)}
        w="400px"
        isRequired
      >
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Seller Approval
        </FormLabel>
        <Controller
          name="sellerApproval"
          control={control}
          render={({ field }) => (
            <Select
              size="md"
              isMulti={false}
              isClearable={false}
              allowMultilineOptions={true}
              placeholder=""
              options={YesNoSelectOptions}
              value={YesNoSelectOptions.find(
                (c) => c.value === Number(field.value)
              )}
              onChange={(option) => {
                if (!option) {
                  return;
                }

                setValue("sellerApproval", Boolean(option.value));
              }}
            />
          )}
        />
        <FormErrorMessage>{errors?.sellerApproval?.message}</FormErrorMessage>
      </FormControl>

      <FormControl w="400px" mt={4}>
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Estimated Reach
        </FormLabel>
        <Input isDisabled type="text" shadow="sm" size="sm" rounded="md" />
      </FormControl>

      <FormControl w="400px" mt={4}>
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Country
        </FormLabel>
        <Input
          isDisabled
          value="USA"
          type="text"
          shadow="sm"
          size="sm"
          rounded="md"
        />
      </FormControl>

      <FormControl w="400px" mt={4}>
        <FormLabel color={liverampColor} fontSize="sm" fontWeight="bold">
          Currency
        </FormLabel>
        <Input
          isDisabled
          value="USD"
          type="text"
          shadow="sm"
          size="sm"
          rounded="md"
        />
      </FormControl>

      <FormControl w="400px" mt={4}>
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Total Number of People that fit Segment Label
        </FormLabel>
        <Input
          isDisabled
          value={0}
          type="text"
          shadow="sm"
          size="sm"
          rounded="md"
        />
      </FormControl>

      <FormControl mt={4} w="400px">
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Activation Destinations Available
        </FormLabel>
        <HStack
          spacing={4}
          border="1px solid"
          borderColor="inherit"
          padding="2"
          shadow="sm"
          rounded="md"
        >
          {allowedPlatforms &&
            allowedPlatforms
              .split(",")
              .map((id) => destinations[Number(id)])
              .map((val) => (
                <Tag key={val} height="22px" size="sm" m="2px">
                  <TagLabel>{val}</TagLabel>
                </Tag>
              ))}
        </HStack>
      </FormControl>

      {audience && (
        <FormControl
          w="400px"
          mt={4}
          isInvalid={!!errors?.liverampAudienceId?.message}
        >
          <FormLabel
            color={componentColors.form.formLabelColor}
            fontSize="sm"
            fontWeight="bold"
          >
            Intentsify Segment Name ID (Internal)
          </FormLabel>
          <Input
            type="text"
            isDisabled
            value={audience.id}
            shadow="sm"
            size="sm"
            rounded="md"
          />
          <FormErrorMessage>
            {errors?.liverampAudienceId?.message}
          </FormErrorMessage>
        </FormControl>
      )}

      <FormControl
        w="400px"
        mt={4}
        isInvalid={!!errors?.liverampAudienceId?.message}
      >
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Liveramp ID
        </FormLabel>
        <Input
          {...register("liverampAudienceId")}
          type="text"
          placeholder=""
          shadow="sm"
          size="sm"
          rounded="md"
        />
        <FormErrorMessage>
          {errors?.liverampAudienceId?.message}
        </FormErrorMessage>
      </FormControl>

      <FormControl
        w="400px"
        mt={4}
        isInvalid={!!errors?.liverampAudienceSegmentId?.message}
      >
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Liveramp Segment ID
        </FormLabel>
        <Input
          {...register("liverampAudienceSegmentId")}
          type="text"
          placeholder=""
          shadow="sm"
          size="sm"
          rounded="md"
        />
        <FormErrorMessage>
          {errors?.liverampAudienceSegmentId?.message}
        </FormErrorMessage>
      </FormControl>

      <FormControl
        w="400px"
        mt={4}
        isInvalid={!!errors?.marketingText?.message}
      >
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Marketing Text
        </FormLabel>
        <Textarea
          {...register("marketingText")}
          placeholder=""
          shadow="sm"
          size="sm"
          rounded="md"
        />
        <FormErrorMessage>{errors?.marketingText?.message}</FormErrorMessage>
      </FormControl>

      <FormControl
        w="400px"
        mt={4}
        isInvalid={!!errors?.marketingHeadline?.message}
      >
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Marketing Headline
        </FormLabel>
        <Textarea
          {...register("marketingHeadline")}
          placeholder=""
          shadow="sm"
          size="sm"
          rounded="md"
        />
        <FormErrorMessage>
          {errors?.marketingHeadline?.message}
        </FormErrorMessage>
      </FormControl>

      <FormControl
        w="400px"
        mt={4}
        isInvalid={!!errors?.marketingCopy?.message}
      >
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Marketing Copy
        </FormLabel>
        <Textarea
          {...register("marketingCopy")}
          placeholder=""
          shadow="sm"
          size="sm"
          rounded="md"
        />
        <FormErrorMessage>{errors?.marketingCopy?.message}</FormErrorMessage>
      </FormControl>

      <FormControl
        mt={4}
        w="400px"
        isInvalid={Boolean(errors?.readyForDistribution?.message)}
      >
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Ready for Distribution to Data Marketplace?
        </FormLabel>
        <Controller
          name="readyForDistribution"
          control={control}
          render={({ field }) => (
            <Select
              size="md"
              isMulti={false}
              isClearable={false}
              allowMultilineOptions={true}
              placeholder=""
              options={YesNoSelectOptions}
              value={YesNoSelectOptions.find(
                (c) => c.value === Number(field.value)
              )}
              onChange={(option) => {
                if (!option) {
                  return;
                }

                setValue("readyForDistribution", Boolean(option.value));
              }}
            />
          )}
        />
        <FormErrorMessage>
          {errors?.readyForDistribution?.message}
        </FormErrorMessage>
      </FormControl>

      <FormControl
        w="400px"
        mt={4}
        isInvalid={!!errors?.numberOfPlatforms?.message}
      >
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Number of Activation Platforms Distributed
        </FormLabel>
        <Input
          {...register("numberOfPlatforms", {
            valueAsNumber: true,
          })}
          type="number"
          placeholder=""
          shadow="sm"
          size="sm"
          rounded="md"
        />
        <FormErrorMessage>
          {errors?.numberOfPlatforms?.message}
        </FormErrorMessage>
      </FormControl>

      {audienceType === AudienceType.CUSTOM && (
        <FormControl
          w="400px"
          mt={4}
          isInvalid={!!errors?.intentModelId?.message}
        >
          <FormLabel
            color={componentColors.form.formLabelColor}
            fontSize="sm"
            fontWeight="bold"
          >
            Intent Model ID
          </FormLabel>
          <Input
            {...register("intentModelId", {
              valueAsNumber: true,
            })}
            type="number"
            placeholder=""
            shadow="sm"
            size="sm"
            rounded="md"
          />
          <FormErrorMessage>{errors?.intentModelId?.message}</FormErrorMessage>
        </FormControl>
      )}

      <FormControl
        w="400px"
        mt={4}
        isInvalid={!!errors?.avgCompetitorCpm?.message}
      >
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Average Competitor CPM
        </FormLabel>
        <Input
          {...register("avgCompetitorCpm")}
          type="text"
          placeholder=""
          shadow="sm"
          size="sm"
          rounded="md"
        />
        <FormErrorMessage>{errors?.avgCompetitorCpm?.message}</FormErrorMessage>
      </FormControl>

      <FormControl
        w="400px"
        mt={4}
        isInvalid={!!errors?.avgCompetitorMediaPct?.message}
      >
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="bold"
        >
          Average Competitor Media
        </FormLabel>
        <Input
          {...register("avgCompetitorMediaPct")}
          type="text"
          placeholder=""
          shadow="sm"
          size="sm"
          rounded="md"
        />
        <FormErrorMessage>
          {errors?.avgCompetitorMediaPct?.message}
        </FormErrorMessage>
      </FormControl>

      <Flex mt={4} justifyContent="flex-end">
        <ButtonGroup>
          <Button
            size="md"
            onClick={() => navigate(CatalogManagerScreenDefinition.navigate())}
          >
            Cancel
          </Button>
          <Button
            size="md"
            type="submit"
            variant="primary-teal"
            isLoading={isLoading}
          >
            {mode === "create" ? "Create" : "Update"}
          </Button>
        </ButtonGroup>
      </Flex>
    </chakra.form>
  );
};

export { AudienceForm as CreateAudienceForm };
